import { useState } from 'react'
import { Plus, Pencil, Trash2, Eye, Download, LogIn, LogOut, X } from 'lucide-react'
import { Button, Combobox, DatePicker, Input, Select, Toggle } from '@/components/ui'
import type { ComboboxOption, SelectOption } from '@/components/ui'
import type { AuditLogQueryParams } from '@/types'
import { format, parseISO } from 'date-fns'
import { cn } from '@/lib/cn'
import { getUsers } from '@/api/services/users.service'
import { AUDIT_MODULE_OPTIONS, auditModuleLabel } from '../constants/auditModules'

export type DatePreset = 'today' | 'week' | 'month' | 'lastMonth'

async function loadUserOptions(query: string): Promise<ComboboxOption[]> {
  const { data } = await getUsers({ page: 1, limit: 100 })
  const q = query.trim().toLowerCase()
  const filtered = q
    ? data.filter(
        (u) =>
          (u.name && u.name.toLowerCase().includes(q)) ||
          (u.email && u.email.toLowerCase().includes(q))
      )
    : data
  return filtered.map((u) => ({
    value: u.id,
    label: u.name ? `${u.name} (${u.email})` : u.email,
  }))
}

export type AuditLogFilterKey =
  | 'action'
  | 'module'
  | 'actorId'
  | 'entityType'
  | 'entityId'
  | 'isFinancial'
  | 'dateFrom'
  | 'dateTo'

export interface AuditLogFiltersProps {
  params: AuditLogQueryParams | undefined
  onApply: (params: AuditLogQueryParams) => void
  onReset: () => void
  onRemoveFilter?: (key: AuditLogFilterKey) => void
  onPresetApply: (preset: DatePreset) => void
}

const ACTION_OPTIONS: SelectOption[] = [
  { value: '', label: 'All actions' },
  { value: 'create', label: 'Create', icon: <Plus className="h-4 w-4 shrink-0" /> },
  { value: 'update', label: 'Update', icon: <Pencil className="h-4 w-4 shrink-0" /> },
  { value: 'delete', label: 'Delete', icon: <Trash2 className="h-4 w-4 shrink-0" /> },
  { value: 'view', label: 'View', icon: <Eye className="h-4 w-4 shrink-0" /> },
  { value: 'export', label: 'Export', icon: <Download className="h-4 w-4 shrink-0" /> },
  { value: 'login', label: 'Login', icon: <LogIn className="h-4 w-4 shrink-0" /> },
  { value: 'logout', label: 'Logout', icon: <LogOut className="h-4 w-4 shrink-0" /> },
]

const DATE_PRESETS: { id: DatePreset; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'This week' },
  { id: 'month', label: 'This month' },
  { id: 'lastMonth', label: 'Last month' },
]

function getActiveFilterChips(
  params: AuditLogQueryParams | undefined,
  moduleLabel: (m: string) => string,
  actionLabel: (a: string) => string
): { key: AuditLogFilterKey; label: string }[] {
  if (!params) return []
  const chips: { key: AuditLogFilterKey; label: string }[] = []
  if (params.action) chips.push({ key: 'action', label: `Action: ${actionLabel(params.action)}` })
  if (params.module) chips.push({ key: 'module', label: `Module: ${moduleLabel(params.module)}` })
  if (params.actorId) chips.push({ key: 'actorId', label: 'Actor: selected' })
  if (params.entityType)
    chips.push({ key: 'entityType', label: `Entity type: ${params.entityType}` })
  if (params.entityId) chips.push({ key: 'entityId', label: `Entity ID: ${params.entityId}` })
  if (params.isFinancial) chips.push({ key: 'isFinancial', label: 'Financial only' })
  if (params.dateFrom) {
    try {
      chips.push({
        key: 'dateFrom',
        label: `From: ${format(parseISO(params.dateFrom), 'dd/MM/yyyy')}`,
      })
    } catch {
      chips.push({ key: 'dateFrom', label: 'From date' })
    }
  }
  if (params.dateTo) {
    try {
      chips.push({ key: 'dateTo', label: `To: ${format(parseISO(params.dateTo), 'dd/MM/yyyy')}` })
    } catch {
      chips.push({ key: 'dateTo', label: 'To date' })
    }
  }
  return chips
}

export function AuditLogFilters({
  params,
  onApply,
  onReset,
  onRemoveFilter,
  onPresetApply,
}: AuditLogFiltersProps): React.ReactElement {
  const [action, setAction] = useState(params?.action ?? '')
  const [module, setModule] = useState(params?.module ?? '')
  const [actorId, setActorId] = useState(params?.actorId ?? '')
  const [entityType, setEntityType] = useState(params?.entityType ?? '')
  const [entityId, setEntityId] = useState(params?.entityId ?? '')
  const [isFinancial, setIsFinancial] = useState(params?.isFinancial ?? false)
  const [dateFrom, setDateFrom] = useState<Date | null>(
    params?.dateFrom ? new Date(params.dateFrom) : null
  )
  const [dateTo, setDateTo] = useState<Date | null>(params?.dateTo ? new Date(params.dateTo) : null)

  // Re-seed the draft fields whenever the applied filters change (chip
  // removed, reset, preset, URL). Adjusted during render rather than in an
  // effect so the fields never paint stale values.
  const appliedKey = JSON.stringify([
    params?.action,
    params?.module,
    params?.actorId,
    params?.entityType,
    params?.entityId,
    params?.isFinancial,
    params?.dateFrom,
    params?.dateTo,
  ])
  const [seededKey, setSeededKey] = useState(appliedKey)
  if (appliedKey !== seededKey) {
    setSeededKey(appliedKey)
    setAction(params?.action ?? '')
    setModule(params?.module ?? '')
    setActorId(params?.actorId ?? '')
    setEntityType(params?.entityType ?? '')
    setEntityId(params?.entityId ?? '')
    setIsFinancial(params?.isFinancial ?? false)
    setDateFrom(params?.dateFrom ? new Date(params.dateFrom) : null)
    setDateTo(params?.dateTo ? new Date(params.dateTo) : null)
  }

  const activeChips = getActiveFilterChips(
    params,
    auditModuleLabel,
    (a) => a.charAt(0).toUpperCase() + a.slice(1).replace(/_/g, ' ')
  )

  const handleApply = (): void => {
    const next: AuditLogQueryParams = {
      page: 1,
      limit: params?.limit ?? 10,
    }
    if (action.trim()) next.action = action.trim()
    if (module.trim()) next.module = module.trim()
    if (actorId.trim()) next.actorId = actorId.trim()
    if (entityType.trim()) next.entityType = entityType.trim()
    if (entityId.trim()) next.entityId = entityId.trim()
    if (isFinancial) next.isFinancial = true
    if (dateFrom) next.dateFrom = `${dateFrom.toISOString().slice(0, 10)}T00:00:00.000Z`
    if (dateTo) next.dateTo = `${dateTo.toISOString().slice(0, 10)}T23:59:59.999Z`
    onApply(next)
  }

  const handleReset = (): void => {
    setAction('')
    setModule('')
    setActorId('')
    setEntityType('')
    setEntityId('')
    setIsFinancial(false)
    setDateFrom(null)
    setDateTo(null)
    onReset()
  }

  return (
    <div className="space-y-4">
      {/* Date range presets */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="mr-1 shrink-0 text-sm font-medium text-gray-600 dark:text-gray-400">
          Quick range:
        </span>
        {DATE_PRESETS.map((preset) => (
          <button
            key={preset.id}
            type="button"
            className={cn(
              'rounded-full border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 transition-colors',
              'hover:border-primary hover:bg-primary/5 hover:text-primary',
              'dark:border-gray-600 dark:text-gray-300 dark:hover:border-primary dark:hover:bg-primary/10 dark:hover:text-primary'
            )}
            onClick={() => onPresetApply(preset.id)}
          >
            {preset.label}
          </button>
        ))}
      </div>

      {/* Filter fields grid */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Select
          id="audit-action"
          label="Action"
          options={ACTION_OPTIONS}
          value={action}
          onChange={setAction}
          placeholder="All actions"
        />
        <Select
          id="audit-module"
          label="Module"
          options={AUDIT_MODULE_OPTIONS}
          value={module}
          onChange={setModule}
          placeholder="All modules"
        />
        <Combobox
          id="audit-actor"
          label="Actor"
          placeholder="Search by name or email"
          loadOptions={loadUserOptions}
          value={actorId}
          onChange={setActorId}
        />
        <Input
          label="Entity type"
          value={entityType}
          onChange={(e) => setEntityType(e.target.value)}
          placeholder="e.g. payroll"
          className="text-sm"
        />
        <Input
          label="Entity ID"
          value={entityId}
          onChange={(e) => setEntityId(e.target.value)}
          placeholder="Entity ID"
          className="text-sm"
        />
        <div className="flex items-end">
          <Toggle
            id="audit-financial"
            label="Financial only"
            checked={isFinancial}
            onChange={setIsFinancial}
          />
        </div>
        <DatePicker id="audit-dateFrom" label="From date" value={dateFrom} onChange={setDateFrom} />
        <DatePicker
          id="audit-dateTo"
          label="To date"
          value={dateTo}
          onChange={setDateTo}
          minDate={dateFrom ?? undefined}
        />
      </div>

      {/* Active filter chips + action buttons */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        {activeChips.length > 0 && onRemoveFilter && (
          <div className="flex flex-wrap gap-2">
            {activeChips.map(({ key, label }) => (
              <span
                key={key}
                className="inline-flex items-center gap-1 rounded-full bg-white py-1 pl-2.5 pr-1 text-xs font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-200 dark:bg-gray-700 dark:text-gray-100 dark:ring-gray-600"
              >
                {label}
                <button
                  type="button"
                  className="rounded-full p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600"
                  onClick={() => onRemoveFilter(key)}
                  aria-label={`Remove ${label}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="ml-auto flex gap-2">
          <Button type="button" variant="primary" onClick={handleApply}>
            Apply
          </Button>
          <Button type="button" variant="secondary" onClick={handleReset}>
            Reset
          </Button>
        </div>
      </div>
    </div>
  )
}

AuditLogFilters.displayName = 'AuditLogFilters'
